// Ведомость: реестр за месяц слева, сетка выбранной ведомости на всю оставшуюся ширину.
//
// ## Почему сетка занимает всё, а не колонку детали
//
// Колонок здесь до девятнадцати: фамилия, пост, часы, тариф, начислено, аванс, две официальные,
// шесть удержаний, две премии, реквизиты, итог. На ноутбуке 1366 после полосы разделов и
// колонки списка остаётся около 740 точек — сетка не помещается физически. Поэтому колонка
// списка сворачивается одним нажатием, и состояние это живёт В АДРЕСЕ: человек, работающий в
// одной ведомости, сворачивает список один раз, а не каждый раз заново.
//
// ## Что человек видит в первую секунду
//
// Реестр периода уже несёт итоги по каждой ведомости, поэтому список и сводка периода
// заполняются из него — до того, как приедет сетка. Сетка догружается под ними, прошлый её срез
// при этом остаётся на экране: спиннер на месте уже показанного числа запрещён (правило 11).
//
// ## Ведомость не выбрана — это НЕ заглушка «выберите объект»
//
// Справа стоит сводка периода: сколько ведомостей, в каких состояниях, сколько людей и сколько
// денег. Это и есть вопрос, с которым финансовый директор открывает раздел в конце месяца, и
// отвечать на него он не должен нажатиями.
//
// ## Два подписанных календаря (правило 9)
//
// Месяц ведомости и связанный с ним официальный период стоят рядом, и оба подписаны датами.
// Границы официального периода НЕ считаются здесь: смещение задаётся настройкой
// `official_period_offset_months`, и посчитанное в браузере однажды молча разойдётся с
// серверным. Поэтому период ищется среди настоящих официальных периодов по тому месяцу
// ведомости, который сервер сам к нему привязал.

import { useId, useRef, useState } from 'react';

import {
  RiArrowDownSLine,
  RiCalculatorLine,
  RiFileList3Line,
  RiLayoutLeftLine,
  RiLockLine,
} from '@remixicon/react';
import { useLocation, useNavigate } from 'react-router';

import { ТянущаясяКолонка } from '@/layout/ТянущаясяКолонка';
import { cn } from '@/utils/cn';
import type { ApiError } from '@/shared/errors/api-error';
import type { OfficialPeriodOut } from '@/shared/api/endpoints/official';
import type { PayrollOut } from '@/shared/api/endpoints/payroll';
import { useAuthStore } from '@/shared/auth/store';
import { Подсказка } from '@/shared/hints/Подсказка';
import { DataGrid, type PastePlan } from '@/shared/grid';
import { usePeriods as useOfficialPeriods } from '@/modules/payroll/official/queries';
import { can } from '@/shared/rbac/permissions';
import { buildSearch, pushPanel, type SearchState } from '@/shared/routing/search';
import { countLabel, pluralRu } from '@/shared/text/plural';
import { formatDataAsOf, parseMoment } from '@/shared/time/format';
import {
  formatPeriodKey,
  formatPeriodTitle,
  месяцИзАдресаИлиРасчётный,
  shiftPeriod,
  type Period,
} from '@/shared/time/period';
import * as Alert from '@/shared/ui/alert';
import * as Badge from '@/shared/ui/badge';
import * as Button from '@/shared/ui/button';
import * as LinkButton from '@/shared/ui/link-button';
import { PeriodStepper } from '@/shared/ui/period-stepper';
import { Block } from '@/shared/ui/block';
import { Metric, MetricRow } from '@/shared/ui/metric';
import { Toolbar, ToolbarField } from '@/shared/ui/toolbar';
import { ErrorAlert } from '@/shared/ui/error-alert';
import { ListRow, ListSkeleton } from '@/shared/ui/list-row';
import { EmptyState, ErrorState, GridSkeleton, ScreenState } from '@/shared/ui/screen-state';
import { useCustomersQuery, useLegalEntitiesQuery, useSitesQuery } from '@/shared/org/queries';
import { SearchFilter } from '@/shared/ui/search-filter';
import * as SegmentedControl from '@/shared/ui/segmented-control';
import { NO_VALUE, SelectField } from '@/shared/ui/select-field';
import { formatKopecks } from '@/shared/money/format';
import { parseRublesToKopecks } from '@/shared/money/parse';

import {
  FILTER_CUSTOMER,
  FILTER_KIND,
  FILTER_LEGAL_ENTITY,
  FILTER_LIST,
  FILTER_MONEY,
  FILTER_PERIOD,
  FILTER_SHEET,
  FILTER_STATUS,
  LIST_HIDDEN,
  MONEY_EMPTY,
  MONEY_PAYABLE,
  PANEL_SHEET_ADD,
  PANEL_SHEET_GENERATE,
  PANEL_SHEET_RECALC,
  PANEL_SHEET_PERSON,
  PANEL_SHEET_REVIEW,
  PANEL_SHEET_TARIFF,
  PANEL_SHEET_ROW,
} from '../route';

import { buildColumns, validateCell } from './columns';
import { useRecalculate, useRegistry, useSetCells, useSheetGrid } from './queries';
import type { RegistryFilterState } from './queries';
import { ApproveBar } from './ApproveBar';
import { ReviewBar, buildReview } from './ReviewBar';
import { ключПоиска, названиеПодходит, разделитьОбъекты, естьДеньги } from './объекты-периода';
import {
  БЕЗ_ГОРОДА,
  запомнитьСвёрнутые,
  естьЧтоГруппировать,
  поГородам,
  свёрнутыеГорода,
  type ГородРеестра,
} from './города';
import { разобратьПричины, type ПричинаВСтроке } from './причины-списка';
import { flattenEmployees, type SheetRow } from './rows';
import { RowsCaption, TotalsBar, buildTotals } from './TotalsBar';

const VIEW_PERMISSION = 'core.payroll.view';
const EDIT_PERMISSION = 'core.payroll.edit';
const OFFICIAL_PERMISSION = 'core.official.view';
const DICTIONARY_PERMISSION = 'core.dictionary.view';

/**
 * Подпись единственной кнопки пересчёта месяца. Одна и та же в шапке и в тексте плашки справа,
 * которая на неё указывает: пока подпись набиралась в двух местах, они могли разойтись.
 */
const подписьПересчёта = (можноПравить: boolean): string =>
  можноПравить ? 'Пересчитать весь период' : 'Почему здесь нули';

export function SheetsScreen({ search }: { search: SearchState }) {
  const location = useLocation();
  const navigate = useNavigate();
  const права = useAuthStore((state) => state.permissions);

  const можноСмотреть = can(права, VIEW_PERMISSION);
  const можноПравить = can(права, EDIT_PERMISSION);

  // Без месяца в адресе открывается расчётный, то есть прошлый: за него платят, а за начавшийся
  // ведомостей ещё нет. Тот же месяц по умолчанию у вкладки «Удержания».
  const период = месяцИзАдресаИлиРасчётный(search.filters[FILTER_PERIOD]);
  const отборСостояния = search.filters[FILTER_STATUS];
  // Поиск и «есть ли деньги» отбирают строки на клиенте: ведомостей за месяц десятки, и серверный
  // отбор ради них означал бы лишний круг по сети на каждую букву.
  const поиск = search.q ?? '';
  const сырыеДеньги = search.filters[FILTER_MONEY];
  const деньги =
    сырыеДеньги === MONEY_PAYABLE || сырыеДеньги === MONEY_EMPTY ? сырыеДеньги : undefined;
  const выбранная = search.filters[FILTER_SHEET] ?? null;
  const списокСвёрнут = search.filters[FILTER_LIST] === LIST_HIDDEN;

  // Сквозные отборы реестра (тз, раздел 5.4.4). Живут в адресе: ссылка на «черновики
  // СтройТреста за май» обязана открываться у коллеги тем же срезом.
  const отборРеестра = {
    status: отборСостояния,
    legal_entity_id: search.filters[FILTER_LEGAL_ENTITY],
    customer_id: search.filters[FILTER_CUSTOMER],
    kind: search.filters[FILTER_KIND],
  };
  const реестр = useRegistry(период, отборРеестра, можноСмотреть);
  const ведомости = реестр.data?.items ?? [];
  // Сколько объектов ещё без ведомости — для строки внизу списка. Реестр берётся БЕЗ отборов (тот
  // же ключ кэша, что у окна создания): с отбором по состоянию «без ведомости» показал бы объекты,
  // у которых она есть, просто не подошла под отбор. Число неизвестно, пока нет и справочника, и
  // реестра, — и тогда строки нет, а не «0».
  const справочникВиден = can(права, DICTIONARY_PERMISSION);
  const объектыМесяца = useSitesQuery({ active: true }, можноСмотреть && справочникВиден);
  const реестрМесяца = useRegistry(период, {}, можноСмотреть);
  // Месяц пуст по-настоящему: реестр БЕЗ отборов приехал и в нём ничего нет.
  const пустойМесяц = реестрМесяца.data !== undefined && реестрМесяца.data.items.length === 0;
  const безВедомости =
    объектыМесяца.data === undefined || реестрМесяца.data === undefined
      ? null
      : реестрМесяца.data.items.length === 0
        ? // В пустом месяце создание предлагает правая колонка и кнопка шапки: третий раз то же
          // самое внизу пустого списка читалось бы как повтор.
          null
        : разделитьОбъекты(объектыМесяца.data.items, реестрМесяца.data.items).безВедомости.length;
  // Сетка тянется ПО АДРЕСУ, а не по строке из списка. Иначе отбор по состоянию, наложенный на
  // список, закрывал бы уже открытую ведомость: человек включает «черновики», чтобы посмотреть,
  // что осталось, — и теряет с экрана ту, в которой работал. Отбор сужает список, а не выбор.
  const сетка = useSheetGrid(выбранная, можноСмотреть);
  const ведомость =
    сетка.data?.payroll ?? ведомости.find((пункт) => пункт.id === выбранная) ?? null;

  /**
   * Когда получены суммы, которые сейчас на экране.
   *
   * `dataUpdatedAt` относится к ТЕКУЩЕМУ ключу запроса, и в момент смены ведомости он равен
   * нулю — при том что на экране в этот момент намеренно лежат данные прошлого ключа
   * (правило 11). Ноль гасил бы пометку «данные на 12:03» ровно в те 300 мс, ради которых она и
   * существует: под новым заголовком висели бы суммы прежней ведомости без единого признака,
   * что это не она.
   */
  const времяСреза = useRef(0);
  function помнимСрез(текущее: number): Date | null {
    if (текущее !== 0) {
      времяСреза.current = текущее;
    }
    return времяСреза.current === 0 ? null : new Date(времяСреза.current);
  }

  /**
   * Момент среза реестра — ИЗ ОТВЕТА СЕРВЕРА, а не с часов браузера.
   *
   * `generated_at` для того в списочном ответе и лежит: пометка «данные на 12:03» существует,
   * чтобы ей верили, а часы на машине человека могут отставать. Прошлое значение помнится по той
   * же причине, что и у сетки: при смене месяца на экране намеренно остаются прежние строки, и
   * пропавшая пометка означала бы старый список без единого признака, что он старый.
   */
  const срезРеестраПамять = useRef<Date | null>(null);
  function срезРеестра(момент: string | undefined): Date | null {
    const разобранный = parseMoment(момент ?? null);
    if (разобранный !== null) {
      срезРеестраПамять.current = разобранный;
    }
    return срезРеестраПамять.current;
  }

  function перейти(следующий: SearchState) {
    void navigate({ pathname: location.pathname, search: buildSearch(следующий) });
  }

  /**
   * Сменить месяц — И СНЯТЬ выбранную ведомость.
   *
   * Ведомость принадлежит месяцу. Оставив её в адресе, мы получаем шапку «Сентябрь 2026» над
   * августовской сеткой, августовский связанный период рядом и ни одной подсвеченной строки в
   * списке — то есть два подписанных календаря начинают врать в один голос. По этому экрану
   * решают, кому доплачивать.
   */
  function сменитьМесяц(следующий: Period) {
    const фильтры: Record<string, string> = {
      ...search.filters,
      [FILTER_PERIOD]: formatPeriodKey(следующий),
    };
    delete фильтры[FILTER_SHEET];
    перейти({ ...search, filters: фильтры, panels: [] });
  }

  function поставитьОтбор(ключ: string, значение: string | null) {
    const фильтры = { ...search.filters };
    if (значение === null) {
      delete фильтры[ключ];
    } else {
      фильтры[ключ] = значение;
    }
    перейти({ ...search, filters: фильтры, panels: [] });
  }

  /**
   * Поиск по названию: записывается в адрес, стопку панелей НЕ трогает.
   *
   * Запись в адрес идёт с задержкой после последней буквы (`SearchFilter`). Человек, набравший
   * название и сразу нажавший «Создать ведомость», не должен потерять открывшееся окно, когда
   * таймер отстучит: запись поверх адреса с пустой стопкой закрыла бы его.
   */
  function поставитьПоиск(значение: string) {
    перейти({ ...search, q: значение === '' ? undefined : значение });
  }

  /** «Показать все ведомости»: снимает ВСЕ отборы списка разом, одним переходом. */
  function снятьВсеОтборы() {
    const фильтры = { ...search.filters };
    for (const ключ of [
      FILTER_STATUS,
      FILTER_KIND,
      FILTER_LEGAL_ENTITY,
      FILTER_CUSTOMER,
      FILTER_MONEY,
    ]) {
      delete фильтры[ключ];
    }
    перейти({ ...search, q: undefined, filters: фильтры, panels: [] });
  }

  function адресВедомости(id: string): string {
    return `${location.pathname}${buildSearch({
      ...search,
      filters: { ...search.filters, [FILTER_SHEET]: id },
      panels: [],
    })}`;
  }

  function открытьПанель(вид: string, id: string) {
    перейти(pushPanel(search, { view: вид, id }));
  }

  if (!можноСмотреть) {
    return (
      <main className='flex min-h-0 min-w-0 flex-1 items-center justify-center bg-bg-white-0 p-5'>
        <EmptyState
          icon={RiLockLine}
          title='Ведомости вам не показываются'
          description='Ведомость видят финансовый отдел, руководство, операционное руководство и администратор. Табель и объекты при этом могут быть доступны: это разные права.'
          action={
            <span className='text-paragraph-sm text-text-sub-600'>
              Нужна по работе — попросите администратора добавить право.
            </span>
          }
        />
      </main>
    );
  }

  return (
    <main className='flex min-h-0 min-w-0 flex-1 flex-col bg-bg-white-0'>
      <ШапкаПериода
        период={период}
        ведомость={ведомость}
        естьВедомости={ведомости.length > 0}
        списокСвёрнут={списокСвёрнут}
        можноПравить={можноПравить}
        onPeriod={сменитьМесяц}
        onToggleList={() => поставитьОтбор(FILTER_LIST, списокСвёрнут ? null : LIST_HIDDEN)}
        onGenerate={() => открытьПанель(PANEL_SHEET_GENERATE, formatPeriodKey(период))}
        onRecalcPeriod={() => открытьПанель(PANEL_SHEET_RECALC, formatPeriodKey(период))}
      />

      <div className='flex min-h-0 flex-1'>
        {!списокСвёрнут && (
          <РеестрПериода
            ведомости={ведомости}
            выбранная={выбранная}
            едет={реестр.isPending}
            ошибка={реестр.error}
            срез={срезРеестра(реестр.data?.generated_at)}
            отбор={отборРеестра}
            пустойМесяц={пустойМесяц}
            поиск={поиск}
            деньги={деньги}
            безВедомости={безВедомости}
            можноПравить={можноПравить}
            статусы={сетка.data?.status_options}
            onFilter={поставитьОтбор}
            onSearch={поставитьПоиск}
            onResetFilters={снятьВсеОтборы}
            onCreate={() => открытьПанель(PANEL_SHEET_GENERATE, formatPeriodKey(период))}
            onRetry={() => void реестр.refetch()}
            адрес={адресВедомости}
          />
        )}

        <div
          className={cn(
            'flex min-h-0 min-w-0 flex-1 flex-col',
            // В узком окне поверхность одна за раз. Правая колонка в нём прячется, пока ведомость
            // не выбрана, и обычно её место занимает список. В пустом месяце список показывать
            // нечего, а объяснение («ведомостей ещё нет») живёт именно справа: прятать его значило
            // оставить на экране белое поле без единого слова.
            выбранная === null && !пустойМесяц && 'узко:hidden',
          )}
        >
          {выбранная === null ? (
            <СводкаПериода
              период={период}
              ведомости={ведомости}
              едет={реестр.isPending}
              ошибка={реестр.error}
              можноПравить={можноПравить}
              onRetry={() => void реестр.refetch()}
              onGenerate={() => открытьПанель(PANEL_SHEET_GENERATE, formatPeriodKey(период))}
            />
          ) : (
            <ScreenState
              data={сетка.data}
              error={сетка.error as never}
              subject='строки ведомости'
              // Пусто считается по ЛЮДЯМ: список в ответе необязательный, и его отсутствие при
              // ненулевых итогах — это «строки не приехали», а не «строк нет». Развилка ниже.
              isEmpty={(данные) => (данные.employees ?? []).length === 0}
              loading={
                <div className='p-5'>
                  <GridSkeleton />
                </div>
              }
              empty={
                <div className='flex flex-1 items-center justify-center p-5'>
                  {(сетка.data?.totals.rows_count ?? 0) > 0 ? (
                    <EmptyState
                      icon={RiFileList3Line}
                      title='Строки ведомости не приехали'
                      description={`Итоги говорят, что строк в ней ${сетка.data?.totals.rows_count}, а самих строк в ответе нет. Это сбой, а не пустая ведомость: повторите запрос.`}
                      action={
                        <Button.Root
                          variant='neutral'
                          mode='stroke'
                          size='small'
                          onClick={() => void сетка.refetch()}
                        >
                          Повторить
                        </Button.Root>
                      }
                    />
                  ) : (
                    <EmptyState
                      icon={RiFileList3Line}
                      title={`В ведомости ${ведомость?.site_name ?? 'аппарата управления'} нет ни одной строки`}
                      description='Строки собираются из назначений на объект за этот месяц. Если людей на объекте нет, ведомость останется пустой — это не ошибка расчёта.'
                    />
                  )}
                </div>
              }
              onRetry={() => void сетка.refetch()}
            >
              {(данные) => (
                <Сетка
                  // Ключ по АДРЕСУ, а не по показанным данным. Каретка, выделение и сказанные
                  // пояснения обязаны сбрасываться в момент переключения, а не когда приедут
                  // новые данные: иначе причина, названная про ячейку прошлой ведомости, висит
                  // над сеткой, которая уже подписана как другая.
                  key={выбранная}
                  данные={данные}
                  // Показанные числа принадлежат ЭТОЙ ведомости или ещё прошлой. Пока идёт
                  // подмена, переходы в панели по идентификаторам из показанного среза уводили
                  // бы в чужую ведомость и упирались в «этого человека здесь нет».
                  актуальна={данные.payroll.id === выбранная}
                  срез={помнимСрез(сетка.dataUpdatedAt)}
                  можноПравить={можноПравить}
                  onOpenPanel={открытьПанель}
                />
              )}
            </ScreenState>
          )}
        </div>
      </div>
    </main>
  );
}

/**
 * Шапка: два подписанных календаря и действия над периодом.
 *
 * Правило 9: на экране ведомости за август одновременно видны «Август 2026» и связанный
 * официальный период с его настоящими датами. Второй календарь — не украшение: именно из него
 * в строки подставляются выплаченная часть и остаток, и человек обязан видеть, из какого куска
 * времени они взялись.
 */
function ШапкаПериода({
  период,
  ведомость,
  естьВедомости,
  списокСвёрнут,
  можноПравить,
  onPeriod,
  onToggleList,
  onGenerate,
  onRecalcPeriod,
}: {
  период: Period;
  ведомость: PayrollOut | null;
  /**
   * Есть ли за период хоть одна ведомость.
   *
   * От этого зависит только «Пересчитать весь период»: пересчитывать, пока нечего, незачем.
   * «Создать ведомость» стоит в шапке ВСЕГДА, и в пустом месяце тоже: до 01.10.2026 её там не
   * было, и заказчик трижды просил «ведомость по одному объекту», не найдя кнопку. Пустое
   * состояние справа при этом называет своё действие другими словами («Выбрать объекты»):
   * две одинаковые кнопки на одном экране человек читает как две разные и ищет между ними
   * разницу (снимок 26.08.2026).
   */
  естьВедомости: boolean;
  списокСвёрнут: boolean;
  можноПравить: boolean;
  onPeriod: (период: Period) => void;
  onToggleList: () => void;
  onGenerate: () => void;
  onRecalcPeriod: () => void;
}) {
  const права = useAuthStore((state) => state.permissions);
  const виденКонтур = can(права, OFFICIAL_PERMISSION);
  const официальные = useOfficialPeriods(undefined, виденКонтур);
  const связанный = связанныйПериод(
    виденКонтур ? официальные.data?.items : null,
    период,
    ведомость,
    официальные.error !== null,
  );
  // Подсказка первого раза стоит на кнопке создания: 23.09.2026 заказчик спросил, можно ли
  // заводить ведомость по одному объекту, а не «сразу все кучей», — а это давно можно, просто
  // с экрана не видно; 29.09 спросил снова. Прежняя подсказка сгорала впустую: показ
  // засчитывается при отрисовке, а кнопка существовала только в месяце с ведомостями. Теперь
  // кнопка на экране всегда, а человеку без права создавать подсказка не показывается вовсе:
  // она объясняла бы то, чего он сделать не может, и тратила бы единственный показ.
  const якорьСоздания = useRef<HTMLButtonElement>(null);

  return (
    <>
      <Подсказка урок='ведомости-галочками' якорь={якорьСоздания} когда={можноПравить} />
      <Toolbar
        actions={
          <>
            {/* Название открытой ведомости в шапке нужно ТОЛЬКО когда реестр свёрнут: иначе оно
              третий раз повторяет то, что видно в выделенной строке реестра слева и в полосе
              состояния под итогами. На ноутбуке 1366 точек эти лишние тридцать знаков
              переносили шапку на вторую строку, а каждая строка шапки — это строка ведомости,
              которой финансовый директор не увидит. */}
            {ведомость !== null && списокСвёрнут && (
              <span className='text-paragraph-xs text-text-sub-600'>
                {ведомость.site_name ?? 'Аппарат управления'} · {ведомость.status_title}
                {ведомость.number !== null && ` · № ${ведомость.number}`}
              </span>
            )}
            <Button.Root
              ref={якорьСоздания}
              variant='neutral'
              mode='stroke'
              size='xsmall'
              // Кнопка не сереет и без права: выключенная не доводит нажатие до обработчика, и
              // человек, у которого прав нет, не получает вообще ничего в ответ. Окно само скажет,
              // кто заводит ведомости, — и это на одно нажатие честнее серого пятна.
              onClick={onGenerate}
            >
              {можноПравить ? 'Создать ведомость' : 'Кто создаёт ведомости'}
            </Button.Root>
            {/* Пересчёт периода стоит РЯДОМ с созданием, и это главное в этой паре. Деньги в
              ведомости появляются не от создания, а отсюда: часы приходят из табеля. До
              17.09.2026 пересчёт жил кнопкой внутри одной ведомости, и заказчик, создав 77
              ведомостей за июль, получил 366 строк нулей и ушёл спрашивать, почему. */}
            {естьВедомости && (
              <Button.Root variant='neutral' mode='stroke' size='xsmall' onClick={onRecalcPeriod}>
                {подписьПересчёта(можноПравить)}
              </Button.Root>
            )}
          </>
        }
      >
        <Button.Root
          variant='neutral'
          mode='ghost'
          size='xsmall'
          onClick={onToggleList}
          aria-label={списокСвёрнут ? 'Показать реестр ведомостей' : 'Свернуть реестр ведомостей'}
        >
          <Button.Icon as={RiLayoutLeftLine} />
          {списокСвёрнут ? 'Реестр' : 'Свернуть'}
        </Button.Root>

        {/* Первый календарь: месяц ведомости. Подписан датой, а не словом «текущий». */}
        <PeriodStepper
          label='Месяц ведомости'
          title={formatPeriodTitle(период)}
          onPrev={() => onPeriod(shiftPeriod(период, -1))}
          onNext={() => onPeriod(shiftPeriod(период, 1))}
        />

        {/* Второй календарь: связанный официальный период, тоже подписанный датами. */}
        <ToolbarField label='Связанный период'>
          <span className='truncate' title={связанный.note}>
            {связанный.title}
            <span className='pl-2 text-paragraph-xs font-normal text-text-sub-600'>
              {связанный.note}
            </span>
          </span>
        </ToolbarField>
      </Toolbar>
    </>
  );
}

/**
 * Подпись второго календаря: настоящие даты периода либо честное объяснение, почему их нет.
 *
 * `null` вместо списка означает «официальный контур этому человеку не показывается вовсе»: это
 * оплаченная строка тз, а не настройка экрана. Молчаливое «считаем…» в этом случае было бы
 * враньём — считать никто не начинал и не начнёт.
 */
export function связанныйПериод(
  периоды: readonly OfficialPeriodOut[] | undefined | null,
  период: Period,
  ведомость: PayrollOut | null,
  ошибка: boolean,
): { title: string; note: string } {
  if (периоды === undefined && ошибка) {
    // «Считаем…» на упавшем запросе — вечное обещание: никто ничего не считает и не начнёт.
    return {
      title: 'не приехал',
      note: 'официальный контур не ответил; откройте его в соседнем представлении',
    };
  }
  if (периоды === null) {
    return {
      title: 'вам не показывается',
      note: 'официальную часть видят финансовый отдел, руководство и администратор',
    };
  }
  if (периоды === undefined) {
    return {
      title: 'считаем…',
      note: 'границы задаёт официальный контур, а не месяц ведомости',
    };
  }
  const поИдентификатору =
    ведомость?.official_period_id == null
      ? undefined
      : периоды.find((пункт) => пункт.id === ведомость.official_period_id);
  if (поИдентификатору !== undefined) {
    return {
      title: поИдентификатору.title,
      // Короткая подпись, а не фраза: полная («связь задаётся настройкой, а не считается в
      // уме») занимала в шапке триста точек и переносила её на вторую строку на ноутбуке.
      // Смысл тот же, а объяснение целиком осталось подсказкой при наведении.
      note: 'задаётся настройкой',
    };
  }

  // Период месяца СУЩЕСТВУЕТ, а связи у ведомости нет. До 06.09.2026 здесь подставлялся
  // найденный по месяцу период, и шапка говорила «15.07.2026 — 14.08.2026» у ведомости,
  // которая с ним не связана вовсе. Человек читал нули в официальных колонках как «этому
  // сотруднику официально не платят» — при том что официальная часть просто не вычиталась,
  // и на карту уходила сумма сверх уже полученной официально.
  //
  // Экран обязан называть состояние, а не подставлять правдоподобное.
  const поМесяцу = периоды.find(
    (пункт) => пункт.payroll_year === период.year && пункт.payroll_month === период.month,
  );
  if (поМесяцу !== undefined) {
    return {
      title: 'не связана',
      note:
        ведомость?.can_edit === false
          ? 'ведомость утвердили до открытия периода: официальная часть в неё не вошла'
          : 'официальная часть не подставляется; пересчитайте ведомость',
    };
  }
  return {
    title: 'периода нет',
    // Короткая подпись по той же причине, что и у найденного периода выше: полная фраза
    // занимала в шапке триста пятьдесят точек, и при свёрнутом реестре — когда рядом
    // появляется ещё и название открытой ведомости — шапка переносилась на вторую строку,
    // а действие уезжало под неё. Месяц назван левее, в собственном календаре.
    note: 'за этот месяц его ещё не завели',
  };
}

/**
 * Порядок строк реестра: сначала ведомости, в которых есть люди, пустые — в конце.
 *
 * Административная по-прежнему стоит первой: она одна на весь период, и искать её среди
 * шестидесяти объектов человеку незачем. Объекты идут в порядке сервера — по названию.
 *
 * Пустые уходят вниз, а не прячутся: число над списком считает и их, и строка «0 человек ·
 * считать некого» остаётся тем местом, откуда в ведомость добавляют человека. Между собой
 * пустые стоят в прежнем порядке.
 *
 * Аудит 23.09.2026 по копии боевой базы: реестр начинался с десяти пустых ведомостей подряд —
 * «Губкинский», «Когалым», «Лянтор» и другие объекты-города, заведённые переносом, на которые
 * в июле и августе никого не назначили (за август пустых 21 из 78). Подряд наверху они стояли
 * из-за базы копии: на маке она сравнивает кириллицу неверно, и названия без пробелов и знаков
 * уходят вперёд. Боевая база заведена с порядком по кодам букв, и там те же пустые рассыпаны
 * по всему списку. В обоих случаях человек пролистывает пустоту, чтобы дойти до денег, поэтому
 * решается это здесь, а не сортировкой базы.
 */
export function порядокРеестра(
  ведомости: readonly PayrollOut[],
  толькоОбъекты: boolean,
): PayrollOut[] {
  const аппарат = толькоОбъекты ? [] : ведомости.filter((пункт) => пункт.is_administrative);
  const объекты = ведомости.filter((пункт) => !пункт.is_administrative);
  const подряд = [...аппарат, ...объекты];
  return [
    ...подряд.filter((пункт) => пункт.totals.employees_count > 0),
    ...подряд.filter((пункт) => пункт.totals.employees_count === 0),
  ];
}

/** Колонка списка: ведомости периода по объектам плюс административная. */
function РеестрПериода({
  ведомости,
  выбранная,
  едет,
  ошибка,
  срез,
  отбор,
  пустойМесяц,
  поиск,
  деньги,
  безВедомости,
  можноПравить,
  статусы,
  onFilter,
  onSearch,
  onResetFilters,
  onCreate,
  onRetry,
  адрес,
}: {
  ведомости: readonly PayrollOut[];
  выбранная: string | null;
  едет: boolean;
  ошибка: unknown;
  срез: Date | null;
  отбор: RegistryFilterState;
  /** За месяц нет ни одной ведомости: в узком окне список тогда уступает место объяснению. */
  пустойМесяц: boolean;
  /** Что набрано в поиске по названию. */
  поиск: string;
  /** «К выплате» или «Без денег»; пусто — все. */
  деньги: string | undefined;
  /** Сколько объектов без ведомости; `null` — неизвестно либо в месяце ведомостей нет вовсе. */
  безВедомости: number | null;
  можноПравить: boolean;
  статусы: Readonly<Record<string, string>> | undefined;
  onFilter: (ключ: string, значение: string | null) => void;
  onSearch: (значение: string) => void;
  onResetFilters: () => void;
  onCreate: () => void;
  onRetry: () => void;
  адрес: (id: string) => string;
}) {
  const юрлица = useLegalEntitiesQuery();
  const заказчики = useCustomersQuery({});

  const строки = порядокРеестра(ведомости, отбор.kind === 'site');
  const запрос = ключПоиска(поиск);
  const показанные = строки.filter(
    (пункт) =>
      названиеПодходит(пункт.site_name ?? 'Аппарат управления', запрос) &&
      (деньги === undefined || (деньги === MONEY_PAYABLE ? естьДеньги(пункт) : !естьДеньги(пункт))),
  );
  // Над списком написано, что перечислено и сколько (правило 2): пока список сужен поиском или
  // переключателем, одного «77 ведомостей» мало, и подпись называет обе цифры.
  const суженоЗдесь = запрос !== '' || деньги !== undefined;
  const естьОтбор = Object.values(отбор).some((значение) => значение !== undefined) || суженоЗдесь;
  // Одна и та же причина у двух ведомостей и больше называется один раз, над списком, а в строках
  // остаётся короткая пометка. Считается по тем строкам, что показаны, а не по всему месяцу.
  const разбор = разобратьПричины(показанные);

  // Города (11.10.2026): объекты одного города стоят вместе под шапкой, которую можно свернуть.
  // Во время поиска групп нет — ищут конкретный объект (подробно в `города.ts`).
  const раскладка = поГородам(показанные);
  const группы = запрос === '' && естьЧтоГруппировать(раскладка) ? раскладка : null;
  const [свёрнуты, setСвёрнуты] = useState<ReadonlySet<string>>(() => свёрнутыеГорода());
  const опознаватель = useId();
  const всеСвёрнуты = группы !== null && группы.города.every((город) => свёрнуты.has(город.ключ));

  function свернуть(ключи: ReadonlySet<string>) {
    setСвёрнуты(ключи);
    запомнитьСвёрнутые(ключи);
  }

  function переключитьГород(ключ: string) {
    const следующие = new Set(свёрнуты);
    if (следующие.has(ключ)) {
      следующие.delete(ключ);
    } else {
      следующие.add(ключ);
    }
    свернуть(следующие);
  }

  // Метка, которая стоит у КАЖДОЙ строки, не различает ничего. На живом экране 26.08.2026 это
  // выглядело так: «Черновик» шестьдесят четыре раза подряд в столбик, и тут же блок «По
  // состояниям: Черновик 64» — один и тот же факт шестьдесят пять раз. Поэтому состояние
  // называется один раз в подписи колонки, а по строкам расходится только тогда, когда
  // строки им ОТЛИЧАЮТСЯ друг от друга.
  const состоянийВСписке = new Set(ведомости.map((пункт) => пункт.status));
  const общееСостояние = состоянийВСписке.size === 1 ? (ведомости[0]?.status_title ?? null) : null;

  const строка = (пункт: PayrollOut) => (
    <СтрокаРеестра
      key={пункт.id}
      ведомость={пункт}
      выбрана={пункт.id === выбранная}
      адрес={адрес(пункт.id)}
      показыватьСостояние={общееСостояние === null}
      причина={разбор.дляСтроки(пункт)}
    />
  );

  return (
    // Ширину колонки человек тянет мышью за правый край (11.10.2026): на мониторе в 2560 точек
    // при 368 названия резались посередине, а справа пустовала половина экрана. По умолчанию на
    // рабочем столе 416 — ширина колонок «Людей» и «Объектов», где в строке тоже названия; в
    // ландшафте планшета прежние 368, иначе ведомости справа не останется места.
    <ТянущаясяКолонка
      ключ='sheets'
      подпись='Ширина списка ведомостей'
      className={cn(
        'flex h-full w-list shrink-0 flex-col border-r border-stroke-soft-200 bg-bg-white-0 широко:w-list-names',
        // В узком окне поверхность одна за раз — те же правила, что у общей колонки списка
        // (`layout/ListPane`). Пока ведомость не выбрана, реестр занимает экран целиком;
        // выбрали — уступает место сетке, а вернуться к нему можно тем же способом, что и
        // раньше: свернуть и развернуть реестр кнопкой в шапке.
        'узко:w-full узко:shrink узко:border-r-0',
        (выбранная !== null || пустойМесяц) && 'узко:hidden',
      )}
    >
      <header className='flex flex-col gap-2 border-b border-stroke-soft-200 px-4 py-3'>
        <div className='flex items-baseline justify-between gap-2'>
          {/* Месяц здесь НЕ повторяется: он стоит календарём в шапке строкой выше. Пока
              повторялся, подпись не помещалась и обрезалась до «0 ведомостей · август 2…». */}
          <h1 className='truncate text-label-sm text-text-strong-950'>
            {/* Пока список едет, счётчика НЕТ. Снимок состояния загрузки 26.08.2026 показал
                «0 ведомостей» поверх скелетов: человек читает готовый ответ «ведомостей нет»
                там, где ответа ещё не существует, и уходит с экрана. Общая колонка списка
                (`ListPane`) так не делает с самого начала — а эта колонка своя, и ошибку,
                от которой общая защищает, повторила целиком. */}
            {едет && ведомости.length === 0
              ? 'Ведомости'
              : суженоЗдесь
                ? `${показанные.length} из ${ведомости.length}\u00a0${pluralRu(ведомости.length, ['ведомости', 'ведомостей', 'ведомостей'])}`
                : countLabel(ведомости.length, ['ведомость', 'ведомости', 'ведомостей'])}
            {общееСостояние !== null && (
              <span className='font-normal text-text-sub-600'> · все «{общееСостояние}»</span>
            )}
          </h1>
          {срез !== null && (
            <span className='shrink-0 text-paragraph-xs text-text-sub-600'>
              {formatDataAsOf(срез)}
            </span>
          )}
        </div>
        {/* Поиск по названию объекта: ведомостей за месяц семьдесят семь, и искать свою глазами
            значит листать. Живёт в адресе (`?q=`), как поиск в остальных списках. */}
        <SearchFilter
          label='Найти объект'
          placeholder='Найти объект'
          value={поиск}
          onApply={onSearch}
        />
        {/* Где есть деньги: ведомости без денег (пустые, не посчитанные, нулевые) уходят одним
            нажатием, и остаются те, по которым выплата. Слова короткие, чтобы три помещались в
            колонку на любой ширине. */}
        <SegmentedControl.Root
          value={деньги ?? 'all'}
          onValueChange={(значение) => onFilter(FILTER_MONEY, значение === 'all' ? null : значение)}
        >
          <SegmentedControl.List aria-label='Какие ведомости показать'>
            <SegmentedControl.Trigger value='all'>Все</SegmentedControl.Trigger>
            <SegmentedControl.Trigger value={MONEY_PAYABLE}>К выплате</SegmentedControl.Trigger>
            <SegmentedControl.Trigger value={MONEY_EMPTY}>Без денег</SegmentedControl.Trigger>
          </SegmentedControl.List>
        </SegmentedControl.Root>
        {/* Четыре отбора тз (раздел 5.4.4) стоят парами: колонка списка узкая, и в столбик
            они съели бы треть высоты до первой строки. Объект отбирать отдельным полем не
            нужно — каждая строка списка И ЕСТЬ объект. */}
        <div className='grid grid-cols-2 gap-2'>
          <SelectField
            id='sheets-status'
            label='Состояние'
            value={отбор.status ?? NO_VALUE}
            options={Object.entries(статусы ?? СОСТОЯНИЯ_ПО_УМОЛЧАНИЮ).map(([код, слово]) => ({
              value: код,
              label: слово,
            }))}
            noneLabel='все'
            onChange={(значение) =>
              onFilter(FILTER_STATUS, значение === NO_VALUE ? null : значение)
            }
            placeholder='все состояния'
          />
          <SelectField
            id='sheets-kind'
            label='Тип'
            value={отбор.kind ?? NO_VALUE}
            options={[
              { value: 'site', label: 'По объектам' },
              { value: 'admin', label: 'Аппарат управления' },
            ]}
            noneLabel='все'
            onChange={(значение) => onFilter(FILTER_KIND, значение === NO_VALUE ? null : значение)}
            placeholder='все типы'
          />
          <SelectField
            id='sheets-entity'
            label='Юрлицо'
            value={отбор.legal_entity_id ?? NO_VALUE}
            options={(юрлица.data?.items ?? []).map((строка) => ({
              value: строка.id,
              label: строка.short_name ?? строка.name,
            }))}
            noneLabel='все'
            pending={юрлица.isPending}
            failed={юрлица.isError}
            failedError={юрлица.error as ApiError | null}
            onChange={(значение) =>
              onFilter(FILTER_LEGAL_ENTITY, значение === NO_VALUE ? null : значение)
            }
            placeholder='все юрлица'
          />
          <SelectField
            id='sheets-customer'
            label='Заказчик'
            value={отбор.customer_id ?? NO_VALUE}
            options={(заказчики.data?.items ?? []).map((строка) => ({
              value: строка.id,
              label: строка.name,
            }))}
            noneLabel='все'
            pending={заказчики.isPending}
            failed={заказчики.isError}
            failedError={заказчики.error as ApiError | null}
            onChange={(значение) =>
              onFilter(FILTER_CUSTOMER, значение === NO_VALUE ? null : значение)
            }
            placeholder='все заказчики'
          />
        </div>
      </header>

      <div className='min-h-0 flex-1 overflow-y-auto'>
        {/* Ошибка поверх уже показанных строк, а не вместо них: данные из кэша дороже свежей
            ошибки, и обрыв сети не имеет права стирать экран (правило 11). */}
        {ошибка !== null && ошибка !== undefined && ведомости.length > 0 && (
          <div className='flex items-center gap-2 border-b border-stroke-soft-200 bg-bg-weak-50 px-4 py-2'>
            <span className='min-w-0 flex-1 truncate text-paragraph-xs text-text-sub-600'>
              Свежий список не приехал, показан прошлый.
            </span>
            <Button.Root variant='neutral' mode='stroke' size='xxsmall' onClick={onRetry}>
              Повторить
            </Button.Root>
          </div>
        )}
        {ошибка !== null && ошибка !== undefined && ведомости.length === 0 ? (
          <ErrorState
            subject='реестр ведомостей за период'
            error={ошибка as never}
            onRetry={onRetry}
          />
        ) : ведомости.length === 0 && едет ? (
          <ListSkeleton rows={8} />
        ) : показанные.length === 0 ? (
          // Без отбора реестр МОЛЧИТ: то же самое уже написано в правой колонке, крупно и с
          // кнопкой. Два одинаковых сообщения на одном экране человек читает как два разных и
          // ищет между ними разницу, которой нет. А вот когда список пуст ОТ отбора, молчать
          // нельзя: причина названа и снимается одним нажатием.
          естьОтбор && (
            <div className='p-4'>
              <EmptyState
                title='Под этот отбор ведомостей нет'
                description='Снимите поиск или отбор, чтобы увидеть остальные.'
                action={
                  <Button.Root
                    variant='neutral'
                    mode='stroke'
                    size='small'
                    onClick={onResetFilters}
                  >
                    Показать все ведомости
                  </Button.Root>
                }
              />
            </div>
          )
        ) : (
          <>
            {/* Причины, общие для нескольких ведомостей, стоят ЗДЕСЬ один раз. До 01.10.2026 одна
                фраза («табеля за этот месяц нет вовсе…») повторялась в 53 строках из 64 и в
                каждой обрезалась меткой состояния. Плашка лежит в прокрутке, а не в шапке
                колонки: список и так начинается ниже четырёх отборов. */}
            {разбор.общие.length > 0 && (
              <div className='mx-4 my-3 flex flex-col gap-1.5 rounded-lg bg-bg-weak-50 px-3 py-2.5'>
                {разбор.общие.map((причина) => (
                  <p key={причина.текст} className='text-paragraph-xs text-text-sub-600'>
                    {причина.сводка}
                  </p>
                ))}
              </div>
            )}
            {группы === null ? (
              показанные.map(строка)
            ) : (
              <>
                <div className='flex items-center justify-between gap-2 border-b border-stroke-soft-200 px-4 py-2 тач:py-3.5'>
                  <span className='text-paragraph-xs text-text-sub-600'>По городам</span>
                  {/* Свернуть все одним нажатием: городов два десятка, и сворачивать их по одному
                      ради того, чтобы открыть один, — двадцать нажатий. */}
                  <LinkButton.Root
                    variant='gray'
                    size='small'
                    // Палец по ссылке в шестнадцать точек не попадает: на планшете область
                    // нажатия расширена до нормы, и строка под неё ровно той же высоты.
                    className='цель-тача relative'
                    onClick={() =>
                      свернуть(
                        всеСвёрнуты
                          ? new Set()
                          : new Set([...свёрнуты, ...группы.города.map((город) => город.ключ)]),
                      )
                    }
                  >
                    {всеСвёрнуты ? 'Развернуть все' : 'Свернуть все'}
                  </LinkButton.Root>
                </div>
                {группы.аппарат.map(строка)}
                {группы.города.map((город, номер) => {
                  const свёрнут = свёрнуты.has(город.ключ);
                  const список = `${опознаватель}-${номер}`;
                  return (
                    <div key={город.ключ}>
                      <ШапкаГорода
                        город={город}
                        свёрнут={свёрнут}
                        список={список}
                        вНёмВыбранная={город.ведомости.some((пункт) => пункт.id === выбранная)}
                        onToggle={() => переключитьГород(город.ключ)}
                      />
                      {/* Открытая ведомость видна и в свёрнутом городе: её открывают ссылкой
                          из окна пересчёта или присланным адресом, а город свернули ещё вчера,
                          и без её строки слева справа открыто непонятно что. */}
                      <div id={список}>
                        {(свёрнут
                          ? город.ведомости.filter((пункт) => пункт.id === выбранная)
                          : город.ведомости
                        ).map(строка)}
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </>
        )}
      </div>

      {/* Строка внизу списка, а не в прокрутке: семьдесят семь ведомостей длиннее экрана, и
          действие, спрятанное в конце прокрутки, никто не находит. Показывается, только когда
          число известно: «0» на месте «не знаем» соврал бы. */}
      {безВедомости !== null && безВедомости > 0 && (
        <div className='shrink-0 border-t border-stroke-soft-200 bg-bg-white-0 тач:безопасно-снизу'>
          <button
            type='button'
            onClick={onCreate}
            className='цель-тача relative flex w-full items-center px-4 py-3 text-left text-paragraph-sm text-text-sub-600 transition duration-200 ease-out hover:bg-bg-weak-50'
          >
            <span>
              Объектов без ведомости: {безВедомости},{' '}
              <span className='text-primary-base underline underline-offset-4'>
                {можноПравить ? 'создать' : 'кто создаёт'}
              </span>
            </span>
          </button>
        </div>
      )}
    </ТянущаясяКолонка>
  );
}

/**
 * Шапка города в реестре: название, сколько ведомостей и стрелка «свернуть».
 *
 * Вид — разделитель списка из набора AlignUI («solid-text»): серая полоса с мелкими прописными.
 * От строки ведомости он отличается с первого взгляда, и это важно: шапку жмут, чтобы свернуть,
 * а строку — чтобы открыть, и спутать их значит уйти не туда.
 *
 * Шапка прилипает к верху списка, пока листают её город: ведомостей в городе бывает два
 * десятка, и на середине без неё уже не видно, чей это объект. Если открытая ведомость лежит в
 * этом городе, название города набрано тёмным: так видно, в каком городе работают.
 */
function ШапкаГорода({
  город,
  свёрнут,
  список,
  вНёмВыбранная,
  onToggle,
}: {
  город: ГородРеестра;
  свёрнут: boolean;
  /** Опознаватель списка ведомостей города, которым шапка управляет. */
  список: string;
  вНёмВыбранная: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type='button'
      aria-expanded={!свёрнут}
      aria-controls={список}
      onClick={onToggle}
      title={
        город.ключ === БЕЗ_ГОРОДА
          ? 'Город указывается в карточке объекта: «Объекты», объект, «Править».'
          : undefined
      }
      className={cn(
        'sticky top-0 z-10 flex w-full items-center gap-1.5 border-b border-stroke-soft-200 bg-bg-weak-50 px-4 py-1.5 text-left transition duration-200 ease-out hover:bg-bg-soft-200',
        // На планшете шапку жмут пальцем: двадцать восемь точек высоты для него мало.
        'тач:py-3',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-base',
      )}
    >
      <RiArrowDownSLine
        aria-hidden='true'
        className={cn(
          'size-4 shrink-0 text-text-sub-600 transition-transform duration-200 ease-out',
          свёрнут && '-rotate-90',
        )}
      />
      <span
        className={cn(
          'min-w-0 flex-1 truncate text-subheading-xs uppercase',
          вНёмВыбранная ? 'text-text-strong-950' : 'text-text-sub-600',
        )}
      >
        {город.название}
      </span>
      <span className='shrink-0 text-subheading-xs tabular-nums text-text-sub-600'>
        {город.ведомости.length}
      </span>
    </button>
  );
}

/**
 * Справочник состояний на случай, когда сетка ещё не приезжала.
 *
 * Сервер шлёт его вместе с сеткой (`status_options`), и рисовать надо ЕГО. Эта копия работает
 * ровно до первой открытой ведомости и нужна затем, чтобы отбор в списке существовал с первой
 * секунды, а не появлялся после выбора ведомости.
 */
const СОСТОЯНИЯ_ПО_УМОЛЧАНИЮ: Readonly<Record<string, string>> = {
  draft: 'Черновик',
  review: 'На согласовании',
  approved: 'Утверждена',
  paid: 'Выплачена',
  closed: 'Закрыта',
};

function СтрокаРеестра({
  ведомость,
  выбрана,
  адрес,
  показыватьСостояние,
  причина,
}: {
  ведомость: PayrollOut;
  выбрана: boolean;
  адрес: string;
  /** Ложь, когда состояние у всех строк одно: тогда его называет подпись колонки. */
  показыватьСостояние: boolean;
  /** Что дописать про причину нуля: пометку, если причина общая, или её целиком, если одна. */
  причина: ПричинаВСтроке;
}) {
  const рассчитана = ведомость.calculated_employees > 0;
  return (
    <ListRow
      to={адрес}
      selected={выбрана}
      // Название объекта переносится, а не режется (11.10.2026): в нём заказчик, адрес и город,
      // и многоточие съедало ровно то, чем объекты различаются.
      wrapTitle
      title={ведомость.site_name ?? 'Аппарат управления'}
      subtitle={
        <>
          {countLabel(ведомость.totals.employees_count, ['человек', 'человека', 'человек'])} ·{' '}
          {/* Три РАЗЛИЧИМЫХ состояния вместо двух, и это не украшение.
              «Ещё не считали» — суммы неизвестны. Ноль по расчёту — ответ, а не пустота.
              Посчитано частично — число есть, но оно не про всех, и молчать об этом нельзя.
              Прежняя строка выводила «посчитана ли» из «итог не ноль» и называла честный ноль
              непосчитанным: человек жал «Пересчитать» на том, что уже посчитано. */}
          {/* «Ещё не считали» рядом с «никого не назначили» противоречит само себе: считать
              там нечего и не будет. Живой снимок боевого экрана 17.09.2026 показал ровно эту
              пару в строке «ВИС г. Астрахань». */}
          {ведомость.totals.employees_count === 0
            ? 'считать некого'
            : рассчитана
              ? formatKopecks(ведомость.totals.total_payout)
              : 'ещё не считали'}
          {/* «У 12 не посчиталось» показывается ТОЛЬКО когда причина не названа отдельной
              строкой. Иначе получалось «у 12 не посчиталось · нет тарифа: 12 строк» — одно и
              то же число дважды в одной строке, и читатель ищет между ними разницу. */}
          {рассчитана &&
            ведомость.blocking_employees > 0 &&
            (ведомость.zero_reason === null || ведомость.zero_reason === undefined) && (
              <span className='text-text-sub-600'>
                {' '}
                · у {ведомость.blocking_employees} не посчиталось
              </span>
            )}
          {/* Причина нуля видна в списке, а не только внутри ведомости: заказчик 16.09.2026
              смотрел именно на этот список — 77 строк с нулями — и заходить в каждую, чтобы
              узнать причину, означало 77 заходов. Общая причина названа над списком один раз, а
              здесь короткая пометка («нет табеля»); причина одной ведомости стоит целиком. */}
          {причина.пометка !== null && <span> · {причина.пометка}</span>}
          {причина.полностью !== null && (
            // `whitespace-normal`: подпись строки обрезается по ширине (`truncate` у `ListRow`), и
            // длинная причина резалась меткой состояния на полуслове («часов вз»). Перенос
            // делает строку выше, но причина читается целиком.
            <span className='block whitespace-normal text-paragraph-xs text-text-sub-600'>
              {причина.полностью}
            </span>
          )}
        </>
      }
      badge={
        показыватьСостояние ? (
          <Badge.Root
            variant='lighter'
            size='medium'
            color={ведомость.status === 'draft' ? 'gray' : 'green'}
          >
            {ведомость.status_title}
          </Badge.Root>
        ) : undefined
      }
    />
  );
}

/**
 * Сводка периода, когда ведомость ещё не выбрана.
 *
 * Не «выберите ведомость», а настоящий экран: в конце месяца финансовый директор приходит
 * сюда с вопросом «где мы стоим по периоду», и ответ на него не должен стоить нажатий.
 */
/** Числа обзора месяца. Вынесены из разметки, чтобы тождество проверялось тестом, а не глазами. */
export function сводкаМесяца(ведомости: readonly PayrollOut[]): {
  людей: number;
  начислено: number;
  /** Всё, что вычли по дороге, одним слагаемым. Отрицательное — доплаты (компенсация билета). */
  вычтено: number;
  кВыплате: number;
  /** Сколько ведомостей уже посчитано: у остальных чисел нет, а не «ноль». */
  рассчитано: number;
  поСостояниям: Map<string, { слово: string; сколько: number }>;
} {
  const поСостояниям = new Map<string, { слово: string; сколько: number }>();
  let людей = 0;
  let начислено = 0;
  let кВыплате = 0;
  let рассчитано = 0;
  for (const пункт of ведомости) {
    const запись = поСостояниям.get(пункт.status) ?? { слово: пункт.status_title, сколько: 0 };
    запись.сколько += 1;
    поСостояниям.set(пункт.status, запись);
    людей += пункт.totals.employees_count;
    начислено += пункт.totals.accrued;
    кВыплате += пункт.totals.total_payout;
    // ⚠️ Признак берётся С СЕРВЕРА, а не выводится из «итог не ноль». Прежняя арифметика
    // считала непосчитанной ведомость, честно посчитанную в ноль, — и человек жал
    // «Пересчитать» на том, что уже посчитано. Ноль по расчёту и «не знаем» — разные вещи.
    if (пункт.calculated_employees > 0) {
      рассчитано += 1;
    }
  }
  // Официальная часть, аванс и удержания одним слагаемым: отдельных чисел в итогах ведомости
  // сервер не отдаёт, а разница — ровно их сумма, и она честно замыкает формулу.
  return { людей, начислено, вычтено: начислено - кВыплате, кВыплате, рассчитано, поСостояниям };
}

function СводкаПериода({
  период,
  ведомости,
  едет,
  ошибка,
  можноПравить,
  onRetry,
  onGenerate,
}: {
  период: Period;
  ведомости: readonly PayrollOut[];
  едет: boolean;
  ошибка: unknown;
  можноПравить: boolean;
  onRetry: () => void;
  onGenerate: () => void;
}) {
  if (ошибка !== null && ошибка !== undefined && ведомости.length === 0) {
    return (
      <div className='p-4'>
        <ErrorState
          subject='реестр ведомостей за период'
          error={ошибка as never}
          onRetry={onRetry}
        />
      </div>
    );
  }
  if (ведомости.length === 0) {
    if (едет) {
      return (
        <div className='p-4'>
          <GridSkeleton />
        </div>
      );
    }
    return (
      <div className='flex flex-1 items-center justify-center p-4'>
        <EmptyState
          icon={RiCalculatorLine}
          title={`За ${formatPeriodTitle(период).toLowerCase()} ведомостей ещё нет`}
          illustration='период'
          // Порядок назван прямо: деньги в ведомости появляются не от создания, а от табеля и
          // пересчёта. До 06.10.2026 здесь стояло «отметьте те, по которым выплата сегодня» —
          // не про ввод прошлого месяца, которым заказчик занят после очистки данных, — а про
          // табель не было ни слова, и созданные ведомости встречали его нулями.
          description={
            можноПравить
              ? `Ведомость заводится отдельно на каждый объект: отметьте нужные, все сразу тоже можно, но с подтверждением. Строки соберутся по назначениям, а суммы появятся, когда в табеле месяца будут часы и вы нажмёте «${подписьПересчёта(true)}».`
              : 'Ведомости заводит финансовый отдел или администратор. Когда они заведут их, ведомости появятся здесь.'
          }
          // Слова ДРУГИЕ, чем у кнопки в шапке («Создать ведомость»): две одинаковые кнопки на
          // одном экране человек читает как две разные и ищет между ними разницу. Человеку без
          // права кнопки здесь нет вовсе: в шапке уже стоит «Кто создаёт ведомости», и второй
          // раз то же самое было бы повтором.
          action={
            можноПравить ? (
              <Button.Root variant='primary' mode='filled' size='small' onClick={onGenerate}>
                Выбрать объекты
              </Button.Root>
            ) : undefined
          }
        />
      </div>
    );
  }

  const { людей, начислено, вычтено, кВыплате, рассчитано, поСостояниям } = сводкаМесяца(ведомости);

  return (
    <div className='flex flex-1 flex-col gap-4 overflow-y-auto p-5'>
      <div className='flex flex-col gap-1'>
        <h2 className='text-label-lg text-text-strong-950'>
          {formatPeriodTitle(период)}:{' '}
          {countLabel(ведомости.length, ['ведомость', 'ведомости', 'ведомостей'])}
        </h2>
        <p className='text-paragraph-sm text-text-sub-600'>
          Строк по людям во всех ведомостях месяца: {людей}. Это не штат: человек, работавший на
          двух объектах, посчитан дважды — у него два независимых расчёта. Чтобы увидеть строки,
          выберите ведомость слева.
        </p>
      </div>

      {/* Белая карточка с рамкой, а не серая заливка: фон этой колонки САМ серый, и на нём
          серые плитки не читались как отдельные — числа выглядели просто висящими в воздухе. */}
      {/* Цепочка вычетов, а не два числа рядом. Числа в ряд человек читает как ФОРМУЛУ, и до
          26.08.2026 она здесь не сходилась: 201 064,51 начислено против 197 064,51 к выплате,
          и четыре тысячи разницы не объяснял никто. Несошедшаяся формула читается не как
          «показано не всё», а как «программа посчитала неверно», — и дальше человек не верит
          ни одному числу на экране (`.claude/rules/ui-views.md`).

          Слагаемое считается разницей и называется одним словом намеренно: в списке ведомостей
          сервер отдаёт только начисленное и итог, а подставить сюда разбивку из сводки нельзя —
          та про ВЕСЬ месяц, а список бывает отобран по состоянию, и числа разошлись бы. */}
      <Block flush title='Сколько по месяцу всего'>
        <MetricRow columns={3} className='p-5'>
          <СводнаяВеличина
            подпись='Начислено по периоду'
            значение={начислено}
            известно={рассчитано > 0}
            пояснение='по тарифным строкам всех ведомостей месяца'
          />
          <СводнаяВеличина
            // Знак действия стоит в ПОДПИСИ, а не отдельной колонкой между числами: колонка
            // не влезает в равномерную сетку и на узком окне осиротеет. Доплата (компенсация
            // билета) делает разницу отрицательной — тогда и слово другое, и знак.
            подпись={вычтено >= 0 ? '− Официальная часть и удержания' : '+ Доплаты'}
            значение={Math.abs(вычтено)}
            известно={рассчитано > 0}
            пояснение='из чего сложилось — во вкладке «Сводка»'
          />
          <СводнаяВеличина
            подпись='= Всего к выплате'
            значение={кВыплате}
            известно={рассчитано > 0}
            пояснение='после каскада удержаний'
            главное
          />
        </MetricRow>
      </Block>

      {рассчитано !== ведомости.length && (
        <Alert.Root variant='lighter' status='information' size='small'>
          <Alert.Icon as={RiCalculatorLine} />
          <span className='flex-1'>
            {/* Пояснение переписано 25.08.2026 вместе с реестром, и ещё раз 26.08.2026 — по
                снимку живого экрана. Оно пересказывало то, что и так написано в каждой строке
                слева («у остальных в списке написано „ещё не считали“»), то есть объясняло
                экран экраном. Осталось только то, чего в списке НЕТ: что с этим делать. */}
            {/* Согласование было набрано руками и на одной ведомости давало «Посчитано 0
                ведомость .» — неверную форму и лишний пробел перед точкой, на ПЕРВОМ экране
                раздела «Расчёт». Правка 03.09.2026 по кадру живого экрана. Здесь не просто
                чинится склонение: фраза «посчитано 0 из 1» о единственной ведомости и так
                ничего не сообщает — если она одна, говорить надо про неё, а не про долю. */}
            {/* ⚠️ Совет переписан 17.09.2026, и вот почему. Прежний звучал «откройте ведомость
                и нажмите „Пересчитать“» — верный для одной ведомости и невыполнимый для
                семидесяти семи. Заказчик 16.09 создал 77 ведомостей за июль, прочитал этот
                совет и не сделал ничего: программа не молчала, она просила семьдесят семь
                одинаковых заходов. Тогда рядом с советом встала своя кнопка.

                С 01.10.2026 кнопки здесь НЕТ, а текст на неё указывает. Две кнопки с одним
                названием на одном экране человек читает как две разные и ищет между ними
                разницу (сквозные проверки «кнопки-объясняют» и «панели» краснели ровно на
                этом), а шапочная нужнее: она есть при любой ширине окна и при открытой
                ведомости, а эта плашка видна только справа от списка и только без выбранной
                ведомости. В узком окне её нет вовсе. */}
            {ведомости.length === 1
              ? 'Ведомость ещё не посчитана: деньги появляются не от создания, а от пересчёта. Часы приходят из табеля объекта.'
              : рассчитано === 0
                ? `Ни одна из ${ведомости.length}\u00a0ведомостей ещё не посчитана. Деньги появляются не от создания, а от пересчёта: часы приходят из табеля объекта.`
                : `Посчитано ${рассчитано} из ${ведомости.length}\u00a0ведомостей. У остальных не хватает данных: причины названы в списке ведомостей.`}{' '}
            {можноПравить
              ? `Чтобы посчитать заново, нажмите «${подписьПересчёта(можноПравить)}» вверху.`
              : `Что с этим делать, объясняет кнопка «${подписьПересчёта(можноПравить)}» вверху.`}
          </span>
        </Alert.Root>
      )}

      {/* Блок появляется, только когда состояний БОЛЬШЕ ОДНОГО. При единственном состоянии он
          пересказывал подпись списка слева («64 ведомости · все „Черновик“») теми же словами и
          тем же числом — один факт, написанный дважды на одном экране. */}
      {поСостояниям.size > 1 && (
        <Block flush title='По состояниям'>
          <ul className='flex flex-col divide-y divide-stroke-soft-200'>
            {[...поСостояниям.entries()].map(([код, запись]) => (
              <li
                key={код}
                className='flex items-baseline justify-between gap-4 px-5 py-2.5 text-paragraph-sm text-text-sub-600'
              >
                <span>{запись.слово}</span>
                <span className='tabular-nums text-text-strong-950'>{запись.сколько}</span>
              </li>
            ))}
          </ul>
        </Block>
      )}
    </div>
  );
}

function СводнаяВеличина({
  подпись,
  значение,
  известно,
  пояснение,
  главное,
}: {
  подпись: string;
  значение: number;
  известно: boolean;
  пояснение: string;
  /** Крупная величина в ряду ровно одна — тот ответ, ради которого экран открыли. */
  главное?: boolean;
}) {
  return (
    <Metric
      label={подпись}
      value={известно ? formatKopecks(значение) : 'ещё не считали'}
      tone={!известно ? 'приглушённый' : главное === true ? 'важный' : 'обычный'}
      // Причина, которая гасит ВСЕ ТРИ величины, называется один раз — полосой под рядом, а
      // не в каждой величине (`.claude/rules/ui-views.md`). Все три гаснут вместе: признак у
      // них общий, и до 03.09.2026 в начале каждого месяца человек читал «расчёта по периоду
      // ещё не было» три раза подряд. В самой величине остаётся короткая пометка «ещё не
      // считали» — её достаточно, чтобы отличить «не знаем» от нуля.
      note={известно ? пояснение : undefined}
    />
  );
}

function Сетка({
  данные,
  актуальна,
  срез,
  можноПравить,
  onOpenPanel,
}: {
  данные: NonNullable<ReturnType<typeof useSheetGrid>['data']>;
  /** Показанные числа принадлежат выбранной ведомости, а не предыдущей. */
  актуальна: boolean;
  срез: Date | null;
  можноПравить: boolean;
  onOpenPanel: (вид: string, id: string) => void;
}) {
  // Подсказка про поведение ячеек указывает на саму сетку ссылкой: обёртка сломала бы
  // гибкую высоту, на которой держится расчёт окна строк.
  const якорьСетки = useRef<HTMLDivElement>(null);
  const [запрет, setЗапрет] = useState<string | null>(null);
  const [план, setПлан] = useState<PastePlan<SheetRow> | null>(null);
  const ячейки = useSetCells(данные.payroll.id);
  const пересчёт = useRecalculate(данные.payroll.id);
  const правится = можноПравить && данные.payroll.can_edit && актуальна;

  /**
   * Сохранить введённую ячейку.
   *
   * Пачкой по ЧЕЛОВЕКУ, а не по ячейке: каскад удержаний применяется к сумме начислений всех
   * его тарифных строк ровно один раз, и сервер после записи пересчитывает человека целиком.
   */
  function сохранить(строка: SheetRow, columnId: string, значение: string) {
    setЗапрет(null);
    const разбор =
      значение === '' ? { ok: true as const, value: 0 } : parseRublesToKopecks(значение);
    if (!разбор.ok) {
      setЗапрет('Сумма не разобралась. Пишите рублями: 5 000 либо 5000,50.');
      return;
    }
    ячейки.mutate(
      {
        employeeId: строка.employee.employee_id,
        body: {
          cells: [{ column_code: columnId, amount: разбор.value }],
          version: строка.employee.version,
        },
      },
      {
        // Итог человека после записи проговаривается вслух: сервер пересчитывает его целиком, и
        // «сохранил премию — изменился итог» это ровно то, ради чего премию и вводят. Молчаливое
        // сохранение заставляет искать новый итог глазами по строке из девятнадцати колонок.
        onSuccess: (обновлённый) =>
          setЗапрет(
            `${строка.employee.employee_name}: записано, итог к выплате стал ${formatKopecks(обновлённый.total_payout)}.`,
          ),
      },
    );
  }

  /** Правило 6: «ляжет N, отклонено M и почему» показывается ДО применения, а не после. */
  function применитьВставку() {
    const текущий = план;
    setПлан(null);
    if (текущий === null || текущий.accepted.length === 0) {
      return;
    }
    // Одна пачка на человека, даже если во вставке его задели трижды: три запроса по одному
    // человеку затрут друг друга, и последний выиграет.
    const поЧеловеку = new Map<
      string,
      { version: number; cells: { column_code: string; amount: number }[] }
    >();
    for (const пункт of текущий.accepted) {
      const разбор =
        пункт.value === '' ? { ok: true as const, value: 0 } : parseRublesToKopecks(пункт.value);
      if (!разбор.ok) {
        continue;
      }
      const ключ = пункт.row.employee.employee_id;
      const прежнее = поЧеловеку.get(ключ) ?? {
        version: пункт.row.employee.version,
        cells: [],
      };
      прежнее.cells.push({ column_code: пункт.columnId, amount: разбор.value });
      поЧеловеку.set(ключ, прежнее);
    }
    for (const [employeeId, тело] of поЧеловеку) {
      ячейки.mutate({ employeeId, body: { cells: тело.cells, version: тело.version } });
    }
  }

  /**
   * Переход в панель — только по числам ТОЙ ЖЕ ведомости.
   *
   * Прошлый срез на экране — это правило 11, а не сбой. Но идентификаторы в нём чужие: нажатие
   * увело бы в панель, которая честно скажет «этого человека в открытой ведомости нет». Живая
   * кнопка, ведущая в тупик, хуже паузы в треть секунды.
   */
  function открыть(вид: string, id: string) {
    if (!актуальна) {
      setЗапрет(
        'Эта ведомость ещё едет: на экране пока числа предыдущей. Панель откроется, как только приедут новые — обычно это доля секунды.',
      );
      return;
    }
    onOpenPanel(вид, id);
  }

  const строки = flattenEmployees(данные.employees);
  const итоги = buildTotals(данные.employees, данные.totals, данные.calculated);
  const проверка = buildReview(
    данные.employees,
    данные.totals,
    данные.calculated,
    данные.payroll.rows_without_rate,
    данные.payroll.official_period_id != null,
  );

  // Каталог собирается на каждый рендер намеренно, без мемоизации: он замыкает переходы в
  // панели, а они пересоздаются каждый рендер — мемоизация с ними в зависимостях не срабатывала
  // бы никогда и только притворялась бы оптимизацией.
  const колонки = buildColumns({
    columns: данные.columns ?? [],
    totals: данные.totals,
    calculated: данные.calculated,
    canEdit: правится,
    blockedReason: !можноПравить
      ? 'Править ведомость может финансовый отдел или администратор.'
      : (данные.payroll.blocked_reason ??
        (актуальна
          ? 'Эта колонка вводом не правится: значение приходит из своего источника — часы из табеля, официальная часть из контура выплат, удержания из долгов человека.'
          : 'Ведомость ещё едет: числа на экране пока от предыдущей, править их нельзя.')),
    periodTitle: данные.payroll.period_title,
    onOpenRow: (строка) => строка.row !== null && открыть(PANEL_SHEET_ROW, строка.row.id),
    onOpenPerson: (строка) => открыть(PANEL_SHEET_PERSON, строка.employee.id),
  });

  return (
    <div className='flex min-h-0 flex-1 flex-col'>
      <ReviewBar
        review={проверка}
        onOpen={() => открыть(PANEL_SHEET_REVIEW, данные.payroll.id)}
        onTariffs={() =>
          открыть(
            PANEL_SHEET_TARIFF,
            `${данные.payroll.period_year}-${String(данные.payroll.period_month).padStart(2, '0')}~${данные.payroll.site_id ?? ''}`,
          )
        }
      />
      <TotalsBar
        totals={итоги}
        footer={
          <>
            <RowsCaption totals={итоги} />
            <div className='flex items-center gap-3'>
              {данные.template !== null && (
                <span className='text-paragraph-xs text-text-sub-600'>
                  Шаблон: {данные.template.title}
                </span>
              )}
              {/* Добавить человека в ведомость (тз, раздел 5.4.4). Ведомость собирается по
                  назначениям на момент создания, и вышедшего пятого числа в ней нет — а
                  заплатить ему надо. До этой кнопки оставалось только пересоздать ведомость. */}
              <Button.Root
                variant='neutral'
                mode='stroke'
                size='xxsmall'
                onClick={() => {
                  if (!правится) {
                    setЗапрет(
                      можноПравить
                        ? (данные.payroll.blocked_reason ??
                            'В эту ведомость сейчас добавить человека нельзя.')
                        : 'Добавляет людей в ведомость финансовый отдел или администратор.',
                    );
                    return;
                  }
                  setЗапрет(null);
                  onOpenPanel(PANEL_SHEET_ADD, данные.payroll.id);
                }}
              >
                Добавить человека
              </Button.Root>
              <Button.Root
                variant='neutral'
                mode='stroke'
                size='xxsmall'
                // Кнопка не сереет: выключенная не доводит нажатие до обработчика, и человек
                // без права жмёт в пустоту. Причина звучит той же полосой, что и отказ по
                // ячейке.
                onClick={() => {
                  if (!правится) {
                    setЗапрет(
                      можноПравить
                        ? (данные.payroll.blocked_reason ??
                            'Эту ведомость сейчас пересчитать нельзя.')
                        : 'Пересчитывает ведомость финансовый отдел или администратор.',
                    );
                    return;
                  }
                  setЗапрет(null);
                  пересчёт.mutate(undefined, {
                    onSuccess: (отчёт) =>
                      setЗапрет(
                        `${отчёт.summary}. Набор настроек расчёта: ${отчёт.config_version}.` +
                          (отчёт.review === undefined || отчёт.review.length === 0
                            ? ' Замечаний нет.'
                            : ` В разбор попало ${отчёт.review.length}: нажмите «Разобрать» в полосе сверху.`),
                      ),
                  });
                }}
              >
                {пересчёт.isPending ? 'Считаем…' : 'Пересчитать'}
              </Button.Root>
              {/* Правило 11: прошлый срез показывается сразу и С ПОМЕТКОЙ ВРЕМЕНИ. Кэш
                  переживает перезагрузку страницы, и суммы месячной давности без даты
                  неотличимы от свежих. */}
              {срез !== null && (
                <span className='text-paragraph-xs text-text-sub-600'>{formatDataAsOf(срез)}</span>
              )}
            </div>
          </>
        }
      />
      {/* Полоса действий стоит В ШАПКЕ, а не под сеткой: строк в ведомости 281, и кнопка под
          ними означала бы прокрутку перед каждым из четырёх нажатий, которыми закрывается
          месяц. */}
      <ApproveBar payroll={данные.payroll} employees={данные.employees} />

      {(данные.columns ?? []).length === 0 && (
        // Ведомость без шаблона — это экран, на котором нет НИ ОДНОЙ денежной колонки. Молча
        // показать список фамилий значит оставить человека гадать, куда делись суммы: он видит
        // людей, видит итог в полосе наверху и не находит, из чего тот сложился.
        <Alert.Root variant='lighter' status='warning' size='small' className='mx-5 mt-2'>
          <Alert.Icon as={RiFileList3Line} />
          <span className='flex-1'>
            У этой ведомости нет шаблона колонок, поэтому денежных колонок в ней не будет — только
            люди и их посты. Шаблон задаётся объекту либо его типу в карточке объекта; пока он не
            задан, суммы считаются, но показать их не в чем.
          </span>
        </Alert.Root>
      )}
      {данные.payroll.blocked_reason != null && (
        <Alert.Root variant='lighter' status='warning' size='small' className='mx-5 mt-2'>
          <Alert.Icon as={RiLockLine} />
          {данные.payroll.blocked_reason}
        </Alert.Root>
      )}
      {/* Сетка и ПОД НЕЙ пояснение — именно в таком порядке, и это не оформление.
          Пояснение над сеткой вставляет себя в поток и сдвигает строки вниз ровно в тот момент,
          когда человек попал по ячейке: между первым и вторым щелчком двойного нажатия ячейка
          уезжает из-под курсора, и раскрытие источника не срабатывает вовсе. Поймано прогоном в
          настоящем браузере 22.08.2026 — ни один юнит-тест этого увидеть не мог. Под сеткой
          полоса отнимает высоту у её низа, а строки остаются на месте: они выкладываются от
          верха. */}
      <div ref={якорьСетки} className='min-h-0 flex-1'>
        <DataGrid<SheetRow>
          rows={строки}
          columns={колонки}
          rowKey={(строка) => строка.id}
          rowLabel={(строка) => строка.employee.employee_name}
          onBlocked={setЗапрет}
          onCommit={правится ? сохранить : undefined}
          validate={validateCell}
          onPaste={
            правится
              ? (разбор) => {
                  setЗапрет(null);
                  setПлан(разбор);
                }
              : () =>
                  // Копирование в буфер сетка умеет, и человек естественно пробует вставить обратно.
                  // Молчание в ответ неотличимо от сломанной программы, поэтому причина звучит той же
                  // полосой, что и отказ по ячейке.
                  setЗапрет(
                    'Вставить в ведомость нельзя: ввод прямо в неё подключается вместе с расчётом. ' +
                      'Часы правятся в табеле объекта, ставка — в карточке назначения, официальная ' +
                      'часть приходит из контура выплат.',
                  )
          }
          onOpenSource={(источник) => открыть(источник.panel, источник.id)}
          emptyText='В этой ведомости нет ни одной строки: на объект за этот месяц никого не назначили. Поставьте людей в разделе «Люди» и нажмите «Пересчитать».'
        />
      </div>
      <Подсказка урок='ячейка-объясняет' якорь={якорьСетки} />

      {план !== null && (
        // Разобранная вставка — тоже ПОД сеткой: полоса над ней сдвинула бы строки ровно тогда,
        // когда человек смотрит, куда именно лягут числа.
        <div className='flex flex-wrap items-center gap-2 border-t border-stroke-soft-200 bg-bg-weak-50 px-5 py-2'>
          <span className='min-w-0 flex-1 text-paragraph-sm text-text-sub-600'>{план.summary}</span>
          <Button.Root
            variant='primary'
            mode='filled'
            size='xxsmall'
            onClick={
              план.accepted.length === 0
                ? () =>
                    setЗапрет(
                      'Из вставленного не ложится ничего. Причины перечислены слева: поправьте кусок и вставьте заново.',
                    )
                : применитьВставку
            }
          >
            Применить
          </Button.Root>
          <Button.Root variant='neutral' mode='stroke' size='xxsmall' onClick={() => setПлан(null)}>
            Отменить
          </Button.Root>
        </div>
      )}
      {ячейки.isPending && (
        // Десять секунд молчания после Enter человек читает как «не сохранилось» и жмёт ещё
        // раз. Сервер после записи пересчитывает ЧЕЛОВЕКА ЦЕЛИКОМ — каскад удержаний по всем его
        // тарифным строкам, — и это не мгновенно. Врать про итог мы при этом не можем: его
        // считает сервер, и подставить сюда своё число значило бы завести второе место, где
        // живёт сумма к выплате.
        <div
          role='status'
          className='border-t border-stroke-soft-200 bg-bg-weak-50 px-5 py-2 text-paragraph-xs text-text-sub-600'
        >
          Записываем и пересчитываем человека целиком: каскад удержаний применяется ко всем его
          строкам сразу. Введённое не потеряется — дождитесь итога.
        </div>
      )}
      {ячейки.error !== null && (
        <div className='border-t border-stroke-soft-200 px-5 py-2'>
          <ErrorAlert error={ячейки.error as never} />
        </div>
      )}
      {пересчёт.error !== null && (
        <div className='border-t border-stroke-soft-200 px-5 py-2'>
          <ErrorAlert error={пересчёт.error as never} />
        </div>
      )}
      {запрет !== null && (
        <div
          role='status'
          className='flex max-h-24 items-start gap-2 overflow-y-auto border-t border-stroke-soft-200 bg-bg-weak-50 px-5 py-2'
        >
          <span className='min-w-0 flex-1 text-paragraph-xs text-text-sub-600'>{запрет}</span>
          <button
            type='button'
            aria-label='Убрать пояснение'
            title='Убрать пояснение'
            onClick={() => setЗапрет(null)}
            className='shrink-0 text-text-sub-600 hover:text-text-strong-950'
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
}
